import {commitMutation, graphql} from 'react-relay'
import type {Environment, RecordSourceProxy} from 'relay-runtime'

import {escapeOptimisticTitleHTML} from '../utils/helpers'
import type {
  AddSubIssueInput,
  setParentMutation,
  setParentMutation$data,
} from './__generated__/setParentMutation.graphql'

export type OptimisticParentIssue = {
  id: string
  title: string
  url: string
  number: number
  state: string
  stateReason?: string | null
  repository: {
    id: string
    nameWithOwner: string
  }
}

function optimisticSetParent(store: RecordSourceProxy, input: AddSubIssueInput, parentIssue: OptimisticParentIssue) {
  if (!input.subIssueId) return

  const subIssueRecord = store.get(input.subIssueId)
  if (!subIssueRecord) return

  // Get or create the parent issue record in the store
  let parentRecord = store.get(parentIssue.id)
  if (!parentRecord) {
    parentRecord = store.create(parentIssue.id, 'Issue')
  }

  // Set parent scalar fields
  parentRecord.setValue(parentIssue.title, 'title')
  // IssuePickerItem doesn't include titleHTML, so use title as the optimistic value;
  // the server response will overwrite with the real HTML when the mutation resolves.
  parentRecord.setValue(escapeOptimisticTitleHTML(parentIssue.title), 'titleHTML')
  parentRecord.setValue(parentIssue.url, 'url')
  parentRecord.setValue(parentIssue.number, 'number')
  parentRecord.setValue(parentIssue.state, 'state')
  parentRecord.setValue(parentIssue.stateReason ?? null, 'stateReason')

  // Set repository on parent
  let repoRecord = store.get(parentIssue.repository.id)
  if (!repoRecord) {
    repoRecord = store.create(parentIssue.repository.id, 'Repository')
  }
  repoRecord.setValue(parentIssue.repository.nameWithOwner, 'nameWithOwner')
  parentRecord.setLinkedRecord(repoRecord, 'repository')

  // Ensure subIssuesSummary exists on parent (required by SubIssuesSummary component)
  const subIssuesSummary = parentRecord.getLinkedRecord('subIssuesSummary')
  if (!subIssuesSummary) {
    const placeholder = store.create(`${parentIssue.id}:subIssuesSummary:optimistic`, 'SubIssuesSummary')
    placeholder.setValue(0, 'total')
    placeholder.setValue(0, 'completed')
    placeholder.setValue(0, 'percentCompleted')
    parentRecord.setLinkedRecord(placeholder, 'subIssuesSummary')
  }

  // Link parent to sub-issue
  subIssueRecord.setLinkedRecord(parentRecord, 'parent')
}

export function setParentMutation({
  environment,
  input,
  onError,
  onCompleted,
  optimisticParentIssue,
}: {
  environment: Environment
  input: AddSubIssueInput
  onError?: (error: Error) => void
  onCompleted?: (response: setParentMutation$data) => void
  optimisticParentIssue?: OptimisticParentIssue
}) {
  return commitMutation<setParentMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation setParentMutation($input: AddSubIssueInput!) @raw_response_type {
        addSubIssue(input: $input) {
          subIssue {
            ...RelationshipsSectionFragment
          }
        }
      }
    `,
    variables: {
      input,
    },
    optimisticUpdater: optimisticParentIssue
      ? store => optimisticSetParent(store, input, optimisticParentIssue)
      : undefined,
    onError: error => onError && onError(error),
    onCompleted: response => onCompleted && onCompleted(response),
  })
}
